import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useFormEditorLayoutContext } from "../context/form-editor-layout-context";
import { useAdminFormEditorPaths } from "../hooks/use-admin-form-editor-paths";
import { useFormEditorStore } from "../state/form-editor-store";
import type { FormRule } from "../types/form-rule-types";
import { DeleteFormRuleConfirm } from "./form-rules/delete-form-rule-confirm";
import {
	type FormRuleEditorFieldOption,
	FormRuleEditorModal,
} from "./form-rules/form-rule-editor-modal";
import { FormRulesList } from "./form-rules/form-rules-list";

export interface FormRulesTabPanelProps {
	/** Set when the user has read-only access (org admin viewing a system type, etc.). */
	isReadOnly?: boolean;
	/**
	 * Host for All rules, sort, and Add rule. `null` while the mode toolbar
	 * has not mounted that slot.
	 */
	actionsHost?: HTMLElement | null;
	/**
	 * When false, the rules list stays mounted but the editor and delete
	 * dialogs close. The URL session is kept so the draft is still there
	 * when the tab is shown again.
	 */
	active?: boolean;
}

interface RulesSearchShape {
	ruleId?: string;
	ruleMode?: "create" | "edit" | "delete";
	ruleTab?: "details";
	presentation?: "drawer" | "modal" | "full";
}

/**
 * Container for the Form Rules tab. Subscribes to the form-editor store, collects
 * the current field options for the editor modal, and translates URL search params
 * (`ruleId`, `ruleMode`, `ruleTab`, `presentation`) into the modal open state.
 * Rule edits stay in-memory until the user clicks Save on the form editor toolbar.
 *
 * URL convention:
 *  - Editor (create / edit): `presentation=modal&ruleMode=create|edit&ruleTab=details[&ruleId=...]`
 *  - Delete confirm:         `presentation=modal&ruleMode=delete&ruleId=...`
 */
export function FormRulesTabPanel({
	isReadOnly: isReadOnlyProp,
	actionsHost,
	active = true,
}: FormRulesTabPanelProps = {}) {
	const paths = useAdminFormEditorPaths();
	const { isReadOnly: isReadOnlyFromContext } = useFormEditorLayoutContext();
	const isReadOnly = isReadOnlyProp ?? isReadOnlyFromContext;
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const search = useSearch({ strict: false }) as RulesSearchShape;

	const formRules = useFormEditorStore((s) => s.formRules);
	const nodes = useFormEditorStore((s) => s.nodes);
	const addFormRule = useFormEditorStore((s) => s.addFormRule);
	const updateFormRule = useFormEditorStore((s) => s.updateFormRule);
	const removeFormRule = useFormEditorStore((s) => s.removeFormRule);
	const setFormRuleEnabled = useFormEditorStore((s) => s.setFormRuleEnabled);

	const fieldOptions = useMemo<FormRuleEditorFieldOption[]>(() => {
		const list: FormRuleEditorFieldOption[] = [];
		for (const node of Object.values(nodes)) {
			if (node.kind !== "field") continue;
			list.push({
				id: node.field.id,
				label: node.field.label || node.field.name || node.field.id,
				dataType: node.field.fieldType,
				options:
					node.field.fieldType === "select" || node.field.fieldType === "radio"
						? node.field.options
						: undefined,
			});
		}
		return list;
	}, [nodes]);

	const pendingDeleteId =
		search.presentation === "modal" &&
		search.ruleMode === "delete" &&
		search.ruleId
			? search.ruleId
			: null;

	const ruleBeingEdited = useMemo(() => {
		if (search.ruleMode !== "edit" || !search.ruleId) return null;
		return formRules.find((r) => r.id === search.ruleId) ?? null;
	}, [formRules, search.ruleId, search.ruleMode]);

	const ruleBeingDeleted = useMemo(() => {
		if (pendingDeleteId == null) return null;
		return formRules.find((r) => r.id === pendingDeleteId) ?? null;
	}, [pendingDeleteId, formRules]);

	const editorSessionKey =
		search.ruleMode === "create"
			? "create"
			: search.ruleMode === "edit" && search.ruleId
				? `edit:${search.ruleId}`
				: null;

	const isEditorModalOpen =
		active &&
		!isReadOnly &&
		search.presentation === "modal" &&
		(search.ruleMode === "create" ||
			(search.ruleMode === "edit" && ruleBeingEdited != null));

	const pushSearch = useCallback(
		(updates: Partial<RulesSearchShape>) => {
			void navigate({
				to: ".",
				search: (prev) => ({ ...prev, ...updates }),
			});
		},
		[navigate],
	);

	const handleAdd = useCallback(() => {
		pushSearch({
			ruleMode: "create",
			ruleId: undefined,
			ruleTab: "details",
			presentation: "modal",
		});
	}, [pushSearch]);

	const handleEdit = useCallback(
		(id: string) => {
			pushSearch({
				ruleMode: "edit",
				ruleId: id,
				ruleTab: "details",
				presentation: "modal",
			});
		},
		[pushSearch],
	);

	const handleDelete = useCallback(
		(id: string) => {
			pushSearch({
				ruleMode: "delete",
				ruleId: id,
				ruleTab: undefined,
				presentation: "modal",
			});
		},
		[pushSearch],
	);

	const closeOverlays = useCallback(() => {
		pushSearch({
			ruleMode: undefined,
			ruleId: undefined,
			ruleTab: undefined,
			presentation: undefined,
		});
	}, [pushSearch]);

	useEffect(() => {
		const staleEdit =
			search.ruleMode === "edit" &&
			Boolean(search.ruleId) &&
			ruleBeingEdited == null;
		const staleDelete =
			search.ruleMode === "delete" &&
			pendingDeleteId != null &&
			ruleBeingDeleted == null;
		const blockedWhileReadOnly =
			isReadOnly &&
			(search.ruleMode === "create" ||
				search.ruleMode === "edit" ||
				search.ruleMode === "delete");
		if (staleEdit || staleDelete || blockedWhileReadOnly) {
			closeOverlays();
		}
	}, [
		closeOverlays,
		isReadOnly,
		pendingDeleteId,
		ruleBeingDeleted,
		ruleBeingEdited,
		search.ruleId,
		search.ruleMode,
	]);

	const handleEditorSubmit = useCallback(
		(rule: FormRule) => {
			if (search.ruleMode === "create") {
				addFormRule(rule);
				toast.success(t(paths.formRules.toast.created));
				closeOverlays();
				return;
			}
			if (search.ruleMode === "edit" && search.ruleId) {
				const updated = updateFormRule(search.ruleId, {
					...rule,
					id: search.ruleId,
				});
				if (!updated) {
					toast.error(t(paths.formRules.toast.updateFailed));
					return;
				}
				toast.success(t(paths.formRules.toast.updated));
			}
			closeOverlays();
		},
		[
			addFormRule,
			closeOverlays,
			paths.formRules.toast.created,
			paths.formRules.toast.updateFailed,
			paths.formRules.toast.updated,
			search.ruleId,
			search.ruleMode,
			t,
			updateFormRule,
		],
	);

	const handleConfirmDelete = useCallback(() => {
		if (pendingDeleteId == null) {
			closeOverlays();
			return;
		}
		if (!removeFormRule(pendingDeleteId)) {
			toast.error(t(paths.formRules.toast.deleteFailed));
			return;
		}
		toast.success(t(paths.formRules.toast.deleted));
		closeOverlays();
	}, [
		pendingDeleteId,
		removeFormRule,
		paths.formRules.toast.deleteFailed,
		paths.formRules.toast.deleted,
		closeOverlays,
		t,
	]);

	return (
		<>
			<FormRulesList
				rules={formRules}
				paths={paths}
				onAdd={handleAdd}
				onEdit={handleEdit}
				onDelete={handleDelete}
				onToggleEnabled={setFormRuleEnabled}
				isReadOnly={isReadOnly}
				actionsHost={actionsHost}
			/>

			<FormRuleEditorModal
				isOpen={isEditorModalOpen}
				mode={search.ruleMode === "edit" ? "edit" : "create"}
				initialRule={ruleBeingEdited}
				fields={fieldOptions}
				paths={paths}
				onSubmit={handleEditorSubmit}
				onCancel={closeOverlays}
				editorSessionKey={editorSessionKey}
			/>

			<DeleteFormRuleConfirm
				isOpen={active && !isReadOnly && ruleBeingDeleted != null}
				ruleName={ruleBeingDeleted?.name ?? ""}
				paths={paths}
				onConfirm={handleConfirmDelete}
				onCancel={closeOverlays}
			/>
		</>
	);
}
